import React, {
    useEffect,
    useLayoutEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useState,
    useCallback,
} from "react";
import { useSelector } from "react-redux";
import Message from "../Message";
import MessageLoader from "../MessageLoader";
import HarrisChainOfThought from "../HarrisChainOfThought";
import {
    selectMessages,
    selectIsAssistantResponding,
    selectIsTyping,
    selectTaskStatusMessage,
    selectActiveRequestId,
    selectActiveRequestHasContent,
} from "../../Selectors/AssistantSelector";
import "./MessageContent.scss"
import Welcome from "../Welcome";

const SCROLL_BOTTOM_THRESHOLD_PX = 48;
const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const requestFrame = (callback) => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
        return window.requestAnimationFrame(callback);
    }
    return setTimeout(callback, 0);
};
const cancelFrame = (id) => {
    if (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(id);
        return;
    }
    clearTimeout(id);
};

const MessageContent = forwardRef(({ isSourceHydrating = false }, ref) => {
    const messages = useSelector(selectMessages);
    const isUserTyping = useSelector(selectIsTyping);
    const isAssistantResponding = useSelector(selectIsAssistantResponding);
    const taskStatusMessage = useSelector(selectTaskStatusMessage);
    const activeRequestId = useSelector(selectActiveRequestId);
    const activeRequestHasContent = useSelector(selectActiveRequestHasContent);
    const [statusTrail, setStatusTrail] = useState([]);

    const messageEndRef = useRef(null);
    const cardContentRef = useRef(null);
    const scrollFrameRef = useRef(null);
    const shouldAutoScrollRef = useRef(true);
    const previousTypes = [];
    const hasPendingRequestWithoutContent = Boolean(activeRequestId && !activeRequestHasContent);

    const getPreviousMsgType = (msgType, index) => {
        previousTypes.push(msgType);
        return index === 0 ? "" : previousTypes[index - 1];
    };

    const getScrollContainer = useCallback(() => {
        const messageContainer = messageEndRef.current;
        const cardContainer = cardContentRef.current;
        const messageIsScrollable =
            !!messageContainer &&
            messageContainer.scrollHeight - messageContainer.clientHeight > 1;
        const cardIsScrollable =
            !!cardContainer && cardContainer.scrollHeight - cardContainer.clientHeight > 1;

        if (messageIsScrollable) return messageContainer;
        if (cardIsScrollable) return cardContainer;
        return messageContainer || cardContainer;
    }, []);

    const scrollToBottom = useCallback(({ smooth = false } = {}) => {
        const container = getScrollContainer();
        if (!container) return;
        const targetTop = container.scrollHeight;
        const shouldUseSmooth = smooth && !prefersReducedMotion();

        if (typeof container.scrollTo === "function") {
            container.scrollTo({
                top: targetTop,
                behavior: shouldUseSmooth ? "smooth" : "auto",
            });
        } else {
            container.scrollTop = targetTop;
        }

        // JSDOM and a few older browsers do not apply element.scrollTo updates reliably.
        if (container.scrollTop !== targetTop) {
            container.scrollTop = targetTop;
        }
        shouldAutoScrollRef.current = true;
    }, [getScrollContainer]);

    const scheduleScrollToBottom = useCallback((force = false) => {
        const container = getScrollContainer();
        if (!container) return;
        if (!force && !shouldAutoScrollRef.current) return;
        if (scrollFrameRef.current) {
            cancelFrame(scrollFrameRef.current);
        }
        scrollFrameRef.current = requestFrame(() => {
            scrollFrameRef.current = null;
            scrollToBottom({ smooth: !force });
        });
    }, [getScrollContainer, scrollToBottom]);

    const handleScroll = useCallback((event) => {
        const container = event?.currentTarget || getScrollContainer();
        if (!container) return;
        const distanceFromBottom =
            container.scrollHeight - (container.scrollTop + container.clientHeight);
        shouldAutoScrollRef.current = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
    }, [getScrollContainer]);

    useImperativeHandle(ref, () => ({
        scrollToBottom: () => scheduleScrollToBottom(true),
    }), [scheduleScrollToBottom]);

    useLayoutEffect(() => {
        scheduleScrollToBottom();
    }, [messages, isAssistantResponding, scheduleScrollToBottom]);

    useLayoutEffect(() => {
        if (!statusTrail.length) return;
        scheduleScrollToBottom();
    }, [statusTrail.length, scheduleScrollToBottom]);

    useEffect(() => {
        return () => {
            if (!scrollFrameRef.current) return;
            cancelFrame(scrollFrameRef.current);
            scrollFrameRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!taskStatusMessage) return;
        if (taskStatusMessage?.type === "task_status_end") {
            setStatusTrail([]);
            return;
        }
        setStatusTrail((prev) => {
            return [...prev, taskStatusMessage];
        });
    }, [taskStatusMessage]);

    useEffect(() => {
        if (hasPendingRequestWithoutContent) {
            return;
        }
        setStatusTrail([]);
    }, [hasPendingRequestWithoutContent]);

    useEffect(() => {
        if (messages.length !== 0) return;
        setStatusTrail([]);
    }, [messages.length]);

    const statusSteps = statusTrail.length
        ? statusTrail.map((status) => ({
            title: status?.taskTitle || status?.stage || "Thinking...",
            items: status?.taskMessage ? [status.taskMessage] : [],
        }))
        : [];
    const showChainOfThought = hasPendingRequestWithoutContent && statusTrail.length > 0;
    const showThinkingLoader = hasPendingRequestWithoutContent && statusTrail.length === 0;

    let mainContent;
    if (isSourceHydrating) {
        mainContent = (
            <div className="conversation-loading">
                <div className="conversation-loading__badge">
                    <span className="conversation-loading__pulse" />
                    <span>Loading source conversation</span>
                </div>
                <div className="conversation-loading__card">
                    <div className="conversation-loading__line conversation-loading__line--wide" />
                    <div className="conversation-loading__line" />
                    <div className="conversation-loading__line conversation-loading__line--short" />
                </div>
            </div>
        );
    } else if (messages.length === 0) {
        mainContent = <Welcome />;
    } else {
        mainContent = (
            <ul className="msg-container" ref={messageEndRef} onScroll={handleScroll}>
                {messages.map((msg, index) => (
                    <Message
                        key={index}
                        msg={msg}
                        previousType={getPreviousMsgType(msg.type, index)}
                        scrollToBottom={scrollToBottom}
                    />

                ))}
                {isUserTyping && <MessageLoader type="You" />}
            </ul>
        );
    }

    return (
        <div className="card-content" ref={cardContentRef} onScroll={handleScroll}>
            {mainContent}
            {showChainOfThought && (
                <div className="cot-wrapper">
                    <HarrisChainOfThought
                        steps={statusSteps}
                    />
                </div>
            )}
            {showThinkingLoader && (
                <div className="thinking-status" role="status" aria-live="polite">
                    <span className="thinking-status__label">Thinking</span>
                    <span className="thinking-status__dots" aria-hidden="true">
                        <span className="thinking-status__dot" />
                        <span className="thinking-status__dot" />
                        <span className="thinking-status__dot" />
                    </span>
                </div>
            )}
        </div>
    );

});

export default MessageContent;
